import {
  parseModelOutputRetractedMessage,
  readModelAttemptKey,
  TerraUIMessageDataPartName,
  type TerraUIMessage,
} from '@terra/shared-dto-chat-types';

type MessagePart = TerraUIMessage['parts'][number];

const readTombstoneAttemptKey = (part: MessagePart): string | undefined =>
  part.type === TerraUIMessageDataPartName
    ? parseModelOutputRetractedMessage(part.data)?.modelAttemptKey
    : undefined;

const isAssistantTextOrReasoningPart = (
  part: MessagePart,
): part is Extract<MessagePart, { type: 'text' | 'reasoning' }> =>
  part.type === 'text' || part.type === 'reasoning';

const isRetractedAssistantPart = ({
  part,
  modelAttemptKey,
}: {
  part: MessagePart;
  modelAttemptKey: string;
}): boolean =>
  isAssistantTextOrReasoningPart(part) &&
  readModelAttemptKey(part.providerMetadata) === modelAttemptKey;

const retractTrailingAttemptParts = ({
  keptParts,
  modelAttemptKey,
}: {
  keptParts: MessagePart[];
  modelAttemptKey: string;
}): MessagePart[] => {
  const firstTrailingIndex = keptParts.reduce(
    (boundaryIndex, part, index) =>
      isAssistantTextOrReasoningPart(part) ? boundaryIndex : index + 1,
    0,
  );

  return [
    ...keptParts.slice(0, firstTrailingIndex),
    ...keptParts
      .slice(firstTrailingIndex)
      .filter(
        (existing) =>
          !isRetractedAssistantPart({ part: existing, modelAttemptKey }),
      ),
  ];
};

const retractMessageParts = (parts: MessagePart[]): MessagePart[] => {
  if (!parts.some((part) => readTombstoneAttemptKey(part) !== undefined)) {
    return parts;
  }

  return parts.reduce<MessagePart[]>((keptParts, part) => {
    const modelAttemptKey = readTombstoneAttemptKey(part);

    if (modelAttemptKey !== undefined) {
      return retractTrailingAttemptParts({ keptParts, modelAttemptKey });
    }

    return [...keptParts, part];
  }, []);
};

export const retractSupersededModelOutput = (
  messages: TerraUIMessage[],
): TerraUIMessage[] => {
  let changed = false;
  const nextMessages = messages.map((message) => {
    if (message.role !== 'assistant') {
      return message;
    }

    const nextParts = retractMessageParts(message.parts);

    if (nextParts === message.parts) {
      return message;
    }

    changed = true;

    return { ...message, parts: nextParts };
  });

  return changed ? nextMessages : messages;
};
